import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { configuration } from "../../../configuration";
import { ITreeItem } from "../../../models";
import { FetchError, GetTeamsTreeDto, GetTeamsTreeNodeDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

const useGetTeamsTreeRequest = (startDateTimeUtc?: DateTime, endDateTimeUtc?: DateTime) => {
  const { getAccessToken } = useAccessToken();

  async function getTeamsTreeRequest(): Promise<GetTeamsTreeDto> {
    const accessToken = await getAccessToken();
    const url = new URL(`${configuration.apiRootUrl}/teams/tree`);

    if (startDateTimeUtc) {
      const startDateTimeUtcISO = startDateTimeUtc.toISO();

      if (startDateTimeUtcISO != null) {
        url.searchParams.append("startDateTimeUtc", startDateTimeUtcISO);
      }
    }

    if (endDateTimeUtc) {
      const endDateTimeUtcISO = endDateTimeUtc.toISO();

      if (endDateTimeUtcISO != null) {
        url.searchParams.append("endDateTimeUtc", endDateTimeUtcISO);
      }
    }

    const response = await fetch(url.href, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { getTeamsTreeRequest };
};

const useGetTeamsTree = (startDateTimeUtc?: DateTime, endDateTimeUtc?: DateTime) => {
  const { getTeamsTreeRequest } = useGetTeamsTreeRequest(startDateTimeUtc, endDateTimeUtc);

  return useQuery({
    queryKey: queryKeys.teams.getTeamsTree(startDateTimeUtc, endDateTimeUtc),
    queryFn: getTeamsTreeRequest,
    select: (data): ITreeItem[] => flattenTeamTree(null, data.teams),
  });
};

function flattenTeamTree(parentGuid: string | null, nodes: GetTeamsTreeNodeDto[]): ITreeItem[] {
  return nodes.flatMap((x): ITreeItem[] => [
    {
      itemId: x.guid,
      label: x.name,
      depth: x.depth,
      parentItemId: parentGuid,
      childrenItemIds: x.connectedTeams.map((y) => y.guid),
    },
    ...flattenTeamTree(x.guid, x.connectedTeams),
  ]);
}

export { useGetTeamsTree };
